import React, { Fragment, useContext, useMemo } from 'react'

import {
  Alert,
  Box,
  Card,
  DateRangePopover,
  CellRenderers,
  Column,
  Loader,
  useAppTheme,
  SimpleTable,
  Typography,
  makeAppStyles,
  DateRangeValue,
  DateRangePresetOption,
  ReportRange,
  LinearProgress,
  SimpleTableProps,
} from '@percept/mui'

import {
  OrgUserEventHookProps,
  useOrgUserEventsTotal,
  useOrgUserEventsTimeseries,
  resolveDateRangeFromUrlState,
  cardLoadingStyles,
  getSecondaryApplicationLabel,
  SecondaryApplication,
  OrgUnitContext,
} from '@percept/app-components'

import {
  ChartData,
  Line,
  LineProps,
  SVGDatumType,
} from '@percept/mui/charts'

import { useUrlState } from '@percept/hooks'

import { get, isArray, partition, sum, uniq } from 'lodash-es'

import { differenceInDays } from 'date-fns'

import {
  separateThousands,
  isoDateUTC,
} from '@percept/utils'

import { getAnalyticsTimeseriesMapping, getRowKey, getTableRows, UsageTotalsRow } from './lib'

import { PlatformUnit, TimeseriesGranularity } from '@percept/types'

import { providerLabelMap } from '@percept/constants'

import { TimelineDatum } from 'types'


const maxDate = new Date()

const useStyles = makeAppStyles( theme => ({
  ...cardLoadingStyles,
  title: {
    display: 'flex',
    alignItems: 'center',
    height: 28,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('lg')]: {
      height: 24,
      fontSize: 20,
    },
    [theme.breakpoints.down('xs')]: {
      height: 'unset',
      fontSize: 18,
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    },
  },
  chartContainer: {
    marginTop: -5,
    marginBottom: -5,
    marginLeft: 6,
  }
}))

const tableColumns: Column<UsageTotalsRow>[] = [
  {
    key: 'org_unit_name',
    label: 'Organisation',
    align: 'left',
  },
  {
    key: 'secondary_application',
    label: 'Page',
    align: 'left',
  },
  {
    key: 'provider',
    label: 'Provider',
    align: 'left',
  },
  {
    key: 'unique_users',
    label: 'Unique Users',
    align: 'right',
  },
  {
    key: 'views',
    label: 'Views',
    align: 'right',
  },
  {
    key: 'views_per_day',
    label: 'Views Per Day',
    align: 'right',
  },
  // NOTE - 'regularity' is currently hidden
  // while we await an API-powered implementation
  // {
  //   key: 'regularity',
  //   label: 'Regularity',
  //   align: 'right',
  // },
  {
    key: 'chartData',
    label: 'Activity Timeseries',
    align: 'center',
    getSortValue: (data: ChartData | string | number | null | undefined) => {
      if( !isArray(data) ){
        return -1
      }
      return sum(data.map(  d => d.value || 0))
    }
  },
]

const displayNA = (
  <span style={{opacity: 0.5}}>N / A</span>
)

const lineProps: Omit<LineProps<TimelineDatum>, 'data'> = {
  /* eslint-disable react/prop-types, react/display-name */
  xScaleType: 'time',
  arbitraryTimeline: true,
  grid: 'rows',
  axisLine: false,
  axisText: false,
  height: 24,
  width: 300,
  verticalMargin: 0,
  numYTicks: 0,
  fillOpacity: 0.25,
  tooltipLabelDatumFormatter: (d) => <ReportRange start={d.start} end={d.end} />
  /* eslint-enable react/prop-types, react/display-name */
}


const analyticsDateRangePresetOptions: DateRangePresetOption[] = [
  'current-year',
  'last-year',
  'last-quarter',
  'last-month',
  'last-week',
  'last-30-days',
  'last-7-days',
  'yesterday',
  'custom',
]


const hasGroupMembers: SimpleTableProps<UsageTotalsRow>['hasGroupMembers'] = (
  row => {
    const viewsSupportingProviderSegmentation: SecondaryApplication[] = [
      'CAMPAIGN_SETUP',
      'INSIGHTS',
      'REPORT_DASHBOARD'
    ]
    return viewsSupportingProviderSegmentation.includes(row.secondary_application)
  }
)


type AnalyticsUrlParams = {
  start?: string | undefined
  end?: string | undefined
  dateRangePreset: DateRangePresetOption
}

export type OrganisationUsageAnalyticsProps = {
  orgUnit?: PlatformUnit
}

export const OrgUnitAnalytics = (): JSX.Element => {
  
  const orgUnit = useContext(OrgUnitContext)

  const appTheme = useAppTheme()
  const color = get(
    appTheme.appComponents.table,
    ['cellChartColors', 'line']
  ) || appTheme.palette.secondary.main

  const classes = useStyles()

  const [state, setState] = useUrlState<AnalyticsUrlParams>({
    dateRangePreset: 'last-30-days',
    start: undefined,
    end: undefined,
  })

  const dateRangePreset = state.dateRangePreset

  const dateRange = resolveDateRangeFromUrlState(state, maxDate)

  const startDateRef = dateRange[0] && dateRange[0].getTime()
  const endDateRef = dateRange[1] && dateRange[1].getTime()

  const numDaysInRange = dateRange && dateRange[0] && dateRange[1] ?
    (startDateRef == endDateRef)
      ? 1
      : Math.abs(differenceInDays(dateRange[1], dateRange[0]))
    : null
  // Balance detail on sparkline against performance of loading large results set
  const granularity: TimeseriesGranularity = numDaysInRange ? (
    (numDaysInRange < 7 * 5) ? 'DAY'
      : (numDaysInRange < 30 * 5) ? 'WEEK'
        : 'MONTH'
  ) : 'DAY'

  const baseHookParams: OrgUserEventHookProps = {
    start: dateRange && dateRange[0] || null,
    end: dateRange && dateRange[1] || null,
    org_unit_id: orgUnit.id,
    include_non_report_series_views: true,
    include_root_org_unit: true,
  }

  const totalHookParams: OrgUserEventHookProps = {
    ...baseHookParams,
    segmentation: ['SECONDARY_APPLICATION'],
  }

  const totalByProviderHookParams: OrgUserEventHookProps = {
    ...baseHookParams,
    segmentation: ['PROVIDER', 'SECONDARY_APPLICATION'],
  }

  const totalQueryResult = useOrgUserEventsTotal(totalHookParams)
  const totalByProviderQueryResult = useOrgUserEventsTotal(totalByProviderHookParams)

  const timeseriesQueryResult = useOrgUserEventsTimeseries({
    ...totalHookParams,
    granularity,
  })
  const timeseriesByProviderQueryResult = useOrgUserEventsTimeseries({
    ...totalByProviderHookParams,
    granularity,
  })

  const timeseriesDataMapping = useMemo(() => {
    return getAnalyticsTimeseriesMapping({
      data: timeseriesQueryResult.data,
      startDate: startDateRef,
      endDate: endDateRef,
      granularity,
      color,
      numDaysInRange,
    })
  }, [startDateRef, endDateRef, color, timeseriesQueryResult.data, granularity, numDaysInRange])

  const timeseriesByProviderDataMapping = useMemo(() => {
    return getAnalyticsTimeseriesMapping({
      data: timeseriesByProviderQueryResult.data,
      startDate: startDateRef,
      endDate: endDateRef,
      granularity,
      color,
      numDaysInRange,
    })
  }, [startDateRef, endDateRef, color, timeseriesByProviderQueryResult.data, granularity, numDaysInRange])

  const { rows, footerRows } = useMemo(() => {
    let rows = getTableRows({
      data: totalQueryResult.data,
      timeseriesMapping: timeseriesDataMapping,
      numDaysInRange,
    })
    // Extract footer rows matching root org unit id if we have any child orgs
    let footerRows: UsageTotalsRow[] | null = null
    const allOrgUnitIds = uniq(
      rows.map( r => r.org_unit_id )
    )
    if( allOrgUnitIds.includes(orgUnit.id) && allOrgUnitIds.length > 1 ){
      const [childRows, parentRows] = partition(rows, r => r.org_unit_id !== orgUnit.id )
      rows = childRows
      footerRows = parentRows
    }
    return {
      rows,
      footerRows,
    }
  }, [totalQueryResult.data, numDaysInRange, timeseriesDataMapping, orgUnit.id])

  const groupedRowMap = useMemo(() => {
    const rows = getTableRows({
      data: totalByProviderQueryResult.data,
      timeseriesMapping: timeseriesByProviderDataMapping,
      numDaysInRange,
    })
    const groupedRowMap: Record<string, UsageTotalsRow[]> = rows.reduce( (acc, row) => {
      const key = getRowKey({...row, provider: null})
      if( !acc[key] ){
        acc[key] = []
      }
      acc[key].push(row)
      return acc
    }, {} as Record<string, UsageTotalsRow[]>)
    return groupedRowMap
  }, [totalByProviderQueryResult, numDaysInRange, timeseriesByProviderDataMapping])

  const orgUsageTotalCellRenderers: CellRenderers<UsageTotalsRow> = {
    /* eslint-disable react/prop-types, react/display-name */
    provider: (props) => (
      <>{ props.provider ? providerLabelMap[props.provider] : displayNA}</>
    ),
    secondary_application: (props) => (
      <>{props.secondary_application ? getSecondaryApplicationLabel(props.secondary_application) : displayNA}</>
    ),
    regularity: (props): JSX.Element => (
      <>{ props.regularity === null ? displayNA : props.regularity }</>
    ),
    views: props => (
      <>{props.views === null ? displayNA : separateThousands(props.views)}</>
    ),
    views_per_day: ({ views_per_day }) => (
      <>{ views_per_day === null ? displayNA : views_per_day }</>
    ),
    chartData: (props): JSX.Element => (
      <>
        { props.chartData ? (
          <Line
            svgClassName={classes.chartContainer}
            key={`${props.org_unit_id}|${props.provider}`}
            data={props.chartData}
            {...lineProps as Omit<LineProps<SVGDatumType>, 'data'>}
            fill={color}
          />
        ) : timeseriesQueryResult.isLoading ? (
          <Loader
            preset='inline'
            color='secondary'
            fontSize={18}
            marginX={1}
            style={{verticalAlign: 'middle'}} />
        ) : displayNA }
      </>
    ),
    /* eslint-enable react/prop-types, react/display-name */
  }

  return (
    <Fragment>
      <Typography className={classes.title} variant='h4'>
        {orgUnit.name} User Analytics
        <Box ml={3}>
          <DateRangePopover
            color='primary'
            dateRangePreset={dateRangePreset}
            dateRangePresetOptions={analyticsDateRangePresetOptions}
            value={dateRange as DateRangeValue}
            onChange={(dateRange, dateRangePreset) => {
              setState({
                dateRangePreset,
                start: dateRangePreset === 'custom' ? isoDateUTC(dateRange[0]) : undefined,
                end: dateRangePreset === 'custom' ? isoDateUTC(dateRange[1]) : undefined,
              })
            }}
            maxDate={maxDate} />
        </Box>
      </Typography>
      { (
        <Box>
          {
            totalQueryResult.error ? (
              <Alert variant='error' message='An error occurred and the data could not be loaded' />
            ) : totalQueryResult.isLoading ? (
              <Loader preset='fullsize' minHeight='16rem' />
            ) : (
              <Card
                className={classes.card}>
                { (totalQueryResult.isRefetching || timeseriesQueryResult.isRefetching) && (
                  <LinearProgress className={classes.refetchIndicator} variant='indeterminate' />
                )}
                <SimpleTable
                  grouped
                  groupedFooter
                  getRowGroup={row => groupedRowMap[row.key] || []}
                  hasGroupMembers={hasGroupMembers}
                  groupColor={appTheme.appComponents.table.groupedRowColors}
                  groupTextColor={appTheme.palette.text.primary}
                  getRowKey={getRowKey}
                  columns={tableColumns}
                  rows={rows}
                  footerRows={footerRows}
                  renderers={orgUsageTotalCellRenderers}
                  sortable
                  pinFirstColumn
                  stickyHeader
                  unsetStickyHeaderZIndex={false}
                  TableContainerProps={{
                    style: {
                      maxHeight: 'calc(100vh - 160px)',
                    }
                  }}
                  wrapCellText={false} />
              </Card>
            )}
        </Box>
      )}
    </Fragment>
  )
}